import type { FC } from 'react';
import { AlertTriangle, Camera, HelpCircle } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  photoAlt: {
    id: 'diagnosisCard.photoAlt',
    defaultMessage: 'Photo of the affected {crop} sent by the farmer',
    description: 'Alternative text for the crop photo shown on the diagnosis card.',
  },
  confidence: {
    id: 'diagnosisCard.confidence',
    defaultMessage: 'Confidence',
    description: 'Label above the bar showing how sure the agronomist is of the suspected cause.',
  },
  bandUncertain: {
    id: 'diagnosisCard.band.uncertain',
    defaultMessage: 'not sure yet',
    description: 'Plain-words reading of a confidence below 50 percent.',
  },
  bandLikely: {
    id: 'diagnosisCard.band.likely',
    defaultMessage: 'likely, not certain',
    description: 'Plain-words reading of a confidence between 50 and 74 percent.',
  },
  bandVeryLikely: {
    id: 'diagnosisCard.band.veryLikely',
    defaultMessage: 'very likely',
    description: 'Plain-words reading of a confidence between 75 and 89 percent.',
  },
  bandSure: {
    id: 'diagnosisCard.band.sure',
    defaultMessage: 'as sure as a photo allows',
    description: 'Plain-words reading of a confidence of 90 percent or more.',
  },
  spread: {
    id: 'diagnosisCard.spread',
    defaultMessage: '{percent}% of the plot affected',
    description: 'Line stating how much of the field shows symptoms.',
  },
  couldAlsoBe: {
    id: 'diagnosisCard.couldAlsoBe',
    defaultMessage: 'Could also be',
    description: 'Heading above the alternative diagnoses considered.',
  },
  severityWatch: {
    id: 'diagnosisCard.severity.watchTag',
    defaultMessage: 'Watch',
    description: 'Severity tag: keep an eye on the problem, no spray needed yet.',
  },
  severityTreat: {
    id: 'diagnosisCard.severity.treatTag',
    defaultMessage: 'Treat now',
    description: 'Severity tag: treat within the current spray window.',
  },
  severityUrgent: {
    id: 'diagnosisCard.severity.urgentTag',
    defaultMessage: 'Urgent',
    description: 'Severity tag: crop loss is likely within a week without action.',
  },
});

interface Differential {
  name: string;
  why?: string;
}

function readDifferentials(value: unknown): Differential[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const name = str(record.name);
    return name ? [{ name, why: optStr(record.why) }] : [];
  });
}

export const DiagnosisCard: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const crop = str(node.props.crop);
  const photoUrl = optStr(node.props.photoUrl);
  const observed = str(node.props.observed);
  const suspect = str(node.props.suspect);
  const confidence = Math.max(0, Math.min(100, Math.round(num(node.props.confidence) ?? 0)));
  const severity = str(node.props.severity);
  const spread = num(node.props.spreadPercent);
  const severityNote = optStr(node.props.severityNote);
  const differentials = readDifferentials(node.props.differentials);
  const photoQualityNote = optStr(node.props.photoQualityNote);
  const confidenceReason = optStr(node.props.confidenceReason);

  /** A percentage alone reads as a verdict; the words next to it say how much
   *  weight a farmer should actually put on the name above. */
  const confidenceBand = intl.formatMessage(
    confidence >= 90
      ? messages.bandSure
      : confidence >= 75
        ? messages.bandVeryLikely
        : confidence >= 50
          ? messages.bandLikely
          : messages.bandUncertain,
  );

  const severityStyle =
    severity === 'urgent'
      ? 'bg-destructive text-destructive-foreground'
      : severity === 'treat_now'
        ? 'bg-warning text-warning-foreground'
        : 'bg-success text-success-foreground';
  const severityText =
    severity === 'urgent'
      ? intl.formatMessage(messages.severityUrgent)
      : severity === 'treat_now'
        ? intl.formatMessage(messages.severityTreat)
        : intl.formatMessage(messages.severityWatch);

  return (
    <div className="w-full overflow-hidden rounded-lg border border-border bg-card">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={intl.formatMessage(messages.photoAlt, { crop })}
          loading="lazy"
          className="aspect-video w-full object-cover"
        />
      ) : null}

      <div className="p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="mb-1 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
              {crop}
            </p>
            <h2
              className="text-balance text-2xl font-bold leading-tight tracking-tight text-foreground"
              translate="no"
            >
              {suspect}
            </h2>
          </div>
          <span
            className={`shrink-0 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-caps ${severityStyle}`}
          >
            {severityText}
          </span>
        </div>

        <p className="mb-4 flex items-start gap-2 text-base leading-relaxed text-foreground">
          <Camera className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">{observed}</span>
        </p>

        <div className="mb-4">
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-muted-foreground">
              {intl.formatMessage(messages.confidence)}
            </span>
            <span className="text-sm text-muted-foreground">
              <span className="mr-1.5 text-lg font-bold tabular-nums text-foreground">
                {confidence}%
              </span>
              {confidenceBand}
            </span>
          </div>
          <div
            className="h-2.5 w-full overflow-hidden rounded-sm bg-muted"
            role="meter"
            aria-valuenow={confidence}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={intl.formatMessage(messages.confidence)}
          >
            <div className="h-full rounded-sm bg-primary" style={{ width: `${confidence}%` }} />
          </div>
          {confidenceReason ? (
            <p className="mt-2 text-sm leading-snug text-muted-foreground">{confidenceReason}</p>
          ) : null}
        </div>

        {spread !== undefined || severityNote ? (
          <div className="mb-4 rounded-md bg-muted p-3.5">
            {spread !== undefined ? (
              <p className="text-base font-semibold tabular-nums text-foreground">
                {intl.formatMessage(messages.spread, { percent: Math.round(spread) })}
              </p>
            ) : null}
            {severityNote ? (
              <p className="text-sm leading-snug text-muted-foreground">{severityNote}</p>
            ) : null}
          </div>
        ) : null}

        {differentials.length > 0 ? (
          <div className="border-t border-border pt-3.5">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
              <HelpCircle className="size-4 shrink-0" aria-hidden />
              {intl.formatMessage(messages.couldAlsoBe)}
            </p>
            <ul className="space-y-1.5">
              {differentials.map((item) => (
                <li key={item.name} className="text-sm text-foreground">
                  <span className="font-semibold" translate="no">
                    {item.name}
                  </span>
                  {item.why ? (
                    <span className="text-muted-foreground"> — {item.why}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {photoQualityNote ? (
          <p className="mt-4 flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3.5 text-sm font-medium text-foreground">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <span className="min-w-0">{photoQualityNote}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};
